/**
 * Landing, lobby, round — and the replay page, which uses the same canvas.
 *
 * React is chrome only. Nothing about the kitchen goes through reconciliation;
 * `render/loop.ts` owns the canvas and reads the feed directly (resolved
 * question 5).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ClientConfig,
  ConsentRecord,
  IceConfig,
  InputRejectionReason,
  PlayerId,
  RoomClosedReason,
} from '@overcooked/shared'
import { CONSENT_REFUSED, STUDY_PATH } from '@overcooked/shared'
import type { FeedSnapshot } from '../feed/feed'
import { useRoundOutcome, useSessionRecording, useVoiceStateReport } from './sessionHooks'
import { EventFeed, REFUSAL_LIFETIME_MS } from '../feed/feed'
import type { Connection, RoomInfo } from '../net/socket'
import { connect, hasStoredSeat } from '../net/socket'
import AdminPage from './admin/AdminPage'
import { ADMIN_PATH } from './admin/api'
import StudyAdminPage from './admin/StudyAdminPage'
import { STUDY_ADMIN_PATH } from './admin/studyApi'
import StudyApp from '../study/StudyApp'
import EditorPage from '../editor/EditorPage'
import { EDITOR_PATH } from '../editor/draft'
import { REPLAY_PATH, ReplayPage } from '../replay/ReplayPage'
import { OraclePage } from '../oracle/OraclePage'
import { oraclePathFor, parseOraclePath } from '../oracle/path'
import { BonusChip } from '../render/BonusChip'
import { buildCatalog } from '../render/catalog'
import { IconCanvas } from '../render/IconCanvas'
import { drawContainer, drawIngredient } from '../render/icons'
import { Stage } from '../render/Stage'
import type { RecipesControl } from '../render/RecipePanel'
import { prefersRecipesOpen } from '../render/RecipePanel'
import { refusalFor } from '../render/reasons'
import { usePeerVoice } from '../voice/usePeerVoice'
import { createAgentVoice, shouldSpeak } from '../voice/speak'
import type { AgentVoice } from '../voice/speak'
import { KEY_TO_DIRECTION, isSystemChord, roundKeysActive } from './keys'
import { MIC_CONSTRAINTS, MicCheck } from './MicCheck'
import { needsMicrophone } from '../audio/recorderCore'
import { ConsentDeclined, ConsentGate } from './ConsentGate'
import { clearConsent, firstStage, gateSteps, saveConsent, storedConsent } from './consent'
import { CREATE_FAILED, createErrorFrom, readInviteQuery } from './landingInvite'
import { closedMessage, fullMessage, malformedCodeMessage } from './roomClosed'
import type { ClosedMessage } from './roomClosed'
import { isRoomFullMessage, parseRoomPath, roomIsFullFor } from './roomEntry'
import type { RoomSummary } from './roomEntry'
import { LatencyGate } from './LatencyGate'
import { endCardFor, phaseWord, prettyMapName } from './display'
import { fetchOracleList, lobbyOracleSuffix, oracleByMap } from './lobbyOracle'
import { seatIdentity } from '../render/theme'
import { RoundEnd } from './RoundEnd'
import { Tutorial, hasSeenTutorial } from './Tutorial'
import { stepsFor } from './tutorialSteps'
import { TUTORIAL_CONFIG, TUTORIAL_MAP_NAME } from './tutorialMap'

/**
 * Mic check, then latency gate, then a seat.
 *
 * The order is deliberate. The microphone is asked for first because it is the
 * only step that needs a decision from the participant; the latency gate is
 * second because it is the one that can turn them away, and turning someone
 * away after they granted permission is kinder than after they were seated
 * opposite a partner who is now waiting alone.
 *
 * The tutorial used to be third, between the gate and the seat. It is on the
 * landing page now, in front of any join at all — which is where the ticket
 * always wanted it and where it stopped being possible to put it only once the
 * round clock learned to wait for a room that is full and clear of setup. A
 * learner is no longer a seat somebody else is waiting on.
 *
 * A solo room skips the first step entirely. The microphone is asked for so
 * two people can talk to each other; with one seat there is nobody on the
 * other end, `usePeerVoice` would find no peer, and the gate would be a
 * permission prompt in exchange for nothing.
 *
 * A multiplayer room puts the recording agreement in front of all of it, and
 * skips it for a browser that already agreed to the current wording. `mic` is
 * also the stage the page starts on, while the room's seat count is unknown.
 *
 * `closed` is terminal and can be reached from any other step: the room does
 * not exist (a stale link, or a reload after it was deleted) or the server
 * closed it. The reason is kept beside the step, in `closedReason`.
 */
type Step = 'consent' | 'declined' | 'mic' | 'latency' | 'playing' | 'excluded' | 'closed' | 'full'

/**
 * The end of the page for a room that no longer exists.
 *
 * A `.notice` rather than an `.error`: nothing went wrong on the player's side,
 * and the only useful thing left to offer is the way back to the start.
 */
function RoomClosed({ reason }: { reason: RoomClosedReason }) {
  return <RoomUnavailable message={closedMessage(reason)} />
}

/**
 * The same end of the page, for any reason there is no seat here: a closed
 * room, a full one, or a link that never named a room at all.
 */
function RoomUnavailable({ message }: { message: ClosedMessage }) {
  return (
    <section className="gate room-closed" role="status">
      <h1>{message.title}</h1>
      <p className="notice">{message.detail}</p>
      <a className="room-closed-home" href="/">
        Back to home
      </a>
    </section>
  )
}

/**
 * The invitation, while the kitchen is under-seated.
 *
 * Two buttons rather than one because the code and the link are handed over
 * differently: the code is what you read out on a call, the link is what you
 * paste into a chat. The code is set large and letter-spaced for the first of
 * those — four characters someone has to hear correctly the first time.
 *
 * `navigator.clipboard` is unavailable on an insecure origin, so the failure
 * has an answer: the code stays selectable text and the button says so instead
 * of silently doing nothing.
 */
function InviteCode({ code }: { code: string }) {
  const [copied, setCopied] = useState<'code' | 'link' | 'failed' | null>(null)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(null), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  const copy = (what: 'code' | 'link') => {
    const text = what === 'code' ? code : `${window.location.origin}/r/${code}`
    void navigator.clipboard
      ?.writeText(text)
      .then(() => setCopied(what))
      .catch(() => setCopied('failed'))
    if (!navigator.clipboard) setCopied('failed')
  }

  return (
    <div className="invite">
      <span className="invite-label">Your game code</span>
      <code className="room-code">{code}</code>
      <div className="invite-actions">
        <button type="button" className="ghost" onClick={() => copy('code')}>
          {copied === 'code' ? 'Copied' : 'Copy code'}
        </button>
        <button type="button" className="ghost" onClick={() => copy('link')}>
          {copied === 'link' ? 'Copied' : 'Copy link'}
        </button>
      </div>
      <p className="hint invite-hint" role="status">
        {copied === 'failed'
          ? "Couldn't copy. Select the code above instead."
          : 'Your partner enters it under “Got a game code?” on the home page.'}
      </p>
    </div>
  )
}

/**
 * "Ask for a model in the other seat."
 *
 * The human-AI condition's whole operational problem, solved with one button.
 * Without it a round needs two people: one to play and one at a terminal typing
 * the room code into an agent process. With it the player opens a kitchen, reads
 * the lobby, and presses a button — and the seat beside them fills forty seconds
 * later, which is how long a model takes to load nothing and a Whisper to load
 * everything.
 *
 * The button only appears where the server says it would do something. Asking is
 * a POST that can start a billable process, so it is off by default and the
 * status endpoint is how a lobby finds out — rather than a flag in the bundle,
 * which would put the answer in the wrong build the first time the box changed.
 */
function AgentSeatInvite({ code }: { code: string }) {
  const [status, setStatus] = useState<{
    available: boolean
    model: string
    running: boolean
    listening: boolean
    lastExit: string | null
  } | null>(null)
  const [asking, setAsking] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const read = (): void => {
      void fetch(`/api/rooms/${code}/agent`)
        .then((response) => (response.ok ? response.json() : null))
        .then((value) => {
          if (!cancelled && value) setStatus(value)
        })
        .catch(() => undefined)
    }
    read()
    // Polled while the lobby is open, because "the agent I asked for has died"
    // is the one thing a player cannot otherwise find out — the seat simply
    // never fills and they wait for a chef that is not coming.
    const timer = setInterval(read, 4000)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [code])

  const ask = useCallback(() => {
    setAsking(true)
    setProblem(null)
    void fetch(`/api/rooms/${code}/agent`, { method: 'POST' })
      .then(async (response) => {
        const body = (await response.json().catch(() => ({}))) as { error?: string }
        if (!response.ok) {
          throw new Error(body.error ?? `Something went wrong (error ${response.status}). Try again.`)
        }
      })
      .catch((err: Error) => setProblem(err.message))
      .finally(() => setAsking(false))
  }, [code])

  if (!status?.available) return null

  return (
    <div className="invite">
      <span className="invite-label">No partner?</span>
      {status.running ? (
        <p className="hint" role="status">
          Your AI chef joins in a few seconds.
        </p>
      ) : (
        <>
          <div className="invite-actions">
            <button type="button" className="ghost" onClick={ask} disabled={asking}>
              {asking ? 'Adding…' : 'Play with an AI chef'}
            </button>
          </div>
          <p className="hint invite-hint" role="status">
            {problem ??
              status.lastExit ??
              (status.listening
                ? 'It hears you and talks out loud.'
                : "It talks out loud but can't hear you.")}
          </p>
        </>
      )}
    </div>
  )
}

function RoomPage({ roomCode, mapName }: { roomCode: string; mapName: string | null }) {
  const feed = useMemo(() => new EventFeed(), [])
  const [room, setRoom] = useState<RoomInfo>({
    playerId: null,
    roomCode: null,
    players: [],
    phase: 'lobby',
    sessionId: null,
    mapName: null,
  })
  const [error, setError] = useState<string | null>(null)
  const [stage, setStage] = useState<Step>('mic')
  /** Why the room is gone, once `stage` is `closed`. */
  const [closedReason, setClosedReason] = useState<RoomClosedReason | null>(null)
  const closeRoom = useCallback((reason: RoomClosedReason) => {
    setClosedReason(reason)
    setStage('closed')
  }, [])
  /**
   * How many seats *this room* has, which is not the same question as how many
   * seats the server was started for.
   *
   * `undefined` while we are still asking; `null` when the lookup failed and
   * we fall back to the box's own player count. A number is the server's
   * answer, and `1` is the only one that changes anything here. A code that
   * names no room is not a count at all: it closes the page (see below).
   */
  const [roomSeats, setRoomSeats] = useState<number | null | undefined>(undefined)
  /** One seat: no partner, no microphone, no voice, and no waiting for either. */
  const solo = roomSeats === 1
  const [excluded, setExcluded] = useState<string | null>(null)
  /**
   * Whether this visit showed the consent gate, which fixes how many steps
   * the gates count. Decided once, when the first gate is chosen, so agreeing
   * does not renumber the screens that follow.
   */
  const [askedConsent, setAskedConsent] = useState(false)
  /**
   * The agreement this visit will join with. Held here as well as in storage,
   * so a profile that refuses storage can still join after agreeing.
   */
  const consentRef = useRef<ConsentRecord | null>(null)
  const [config, setConfig] = useState<ClientConfig | null>(null)
  const [ice, setIce] = useState<IceConfig | null>(null)
  const [micStream, setMicStream] = useState<MediaStream | null>(null)
  const micStreamRef = useRef<MediaStream | null>(null)
  micStreamRef.current = micStream
  const [connection, setConnection] = useState<Connection | null>(null)
  // The newest input the server threw away. Kept here as well as in the feed
  // because the feed's flash line lives in the HUD, and the HUD only exists
  // while a round is running — a press refused in the lobby has to be answered
  // somewhere the lobby can see.
  const [rejected, setRejected] = useState<InputRejectionReason | null>(null)
  /**
   * The server's 3-2-1, or `null` when nothing is imminent.
   *
   * The number is the server's to say. A client that counted its own three
   * seconds would be telling the player a start time it does not control, and
   * the two windows would drift apart by exactly one network round trip —
   * which is the thing the countdown exists to make agree.
   */
  const [countdown, setCountdown] = useState<number | null>(null)
  const connectionRef = useRef<Connection | null>(null)
  /**
   * Whether this page reclaimed a seat it already had.
   *
   * Read by the gates, which have nothing left to ask, and by the join at the
   * latency pass, which must not run — a second `joinRoom` from a tab that is
   * already seated would take a *second* seat and lock the returning player out
   * of the round they are already in.
   */
  const resumedRef = useRef(false)
  /**
   * The agent seat's voice, when there is one. `null` in every other round.
   *
   * A ref rather than state because the only thing that reads it is a socket
   * handler installed once, and rebuilding the handler when a voice appears
   * would mean tearing down and re-opening the socket mid-round. The identity of
   * the player is read the same way, for the same reason.
   */
  const agentVoiceRef = useRef<AgentVoice | null>(null)
  const selfIdRef = useRef<PlayerId | null>(null)
  selfIdRef.current = room.playerId
  const { ended, tally } = useRoundOutcome(feed)
  // Static content, not game state: the ingredient DAG and the dish catalogue
  // the renderer draws icons from. `/replay` builds the identical object, which
  // is what keeps the two pages one renderer.
  const catalog = useMemo(() => buildCatalog(config), [config])
  useSessionRecording(
    feed,
    stage === 'playing' ? micStream : null,
    room.phase,
    room.sessionId,
    connectionRef,
  )

  useEffect(() => {
    connectionRef.current = connection
  }, [connection])

  /**
   * The test probe — `?probe=1` only, and nothing else in the page knows it exists.
   *
   * The kitchen is a canvas, so a browser harness can assert on the HUD's DOM
   * (`.badge-clock-value`, `#recipe-panel`) and on nothing else. That is enough
   * to see a round clock lie but not enough to see a chop bar lie, and the chop
   * bar is the thing `render/tickClock.ts` exists to fix. This is the narrowest
   * hole that lets `scripts/e2e-browser-hold.mjs` watch the same number the bars
   * are drawn from: one read-only accessor onto `feed.snapshot()`, mounted only
   * when the query string asks for it, so a real session never carries it.
   *
   * Read-only on purpose. A probe that could *drive* the game would be a second
   * input path, and the whole claim of the harness is that it plays the way a
   * player does — through the keyboard.
   */
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('probe') !== '1') return
    const target = window as unknown as { __overcooked_probe?: () => FeedSnapshot }
    target.__overcooked_probe = () => feed.snapshot()
    return () => {
      delete target.__overcooked_probe
    }
  }, [feed])

  // The gate threshold is a tuning decision that lives in timings.json, so the
  // client asks the server for it rather than carrying a copy.
  useEffect(() => {
    let cancelled = false
    void fetch('/api/config')
      .then((response) => response.json() as Promise<ClientConfig>)
      .then((value) => {
        if (!cancelled) setConfig(value)
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't reach the game. Check your connection and reload.")
      })
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * Ask the room how many seats it has, before the first gate is drawn.
   *
   * It has to be before: the mic gate is the first thing a player sees and the
   * whole of solo mode's client half is not showing it. `/api/rooms/:code` is
   * the same lookup the join box on the landing page makes, and it answers
   * 404 for a code that names no room. Joins no longer create rooms, so that
   * is a stale link and the page says so at once, rather than walking the
   * player through a microphone check for a game that is not there. The join
   * would reach the same answer (`roomClosed` / `not-found`); this is sooner.
   */
  useEffect(() => {
    let cancelled = false
    /** The page has already been decided — closed, or full — and no gate follows. */
    const HANDLED = 'handled' as const
    void fetch(`/api/rooms/${roomCode}`)
      .then(async (response) => {
        if (response.status === 404) return 'missing' as const
        if (!response.ok) throw new Error(String(response.status))
        return (await response.json()) as RoomSummary
      })
      .then((summary) => {
        if (cancelled) return HANDLED
        if (summary === 'missing') {
          // A stale link. The page ends here; the gates below are never drawn,
          // because `closeRoom` moves the stage off `mic`.
          setRoomSeats(null)
          closeRoom('not-found')
          return HANDLED
        }
        // Every seat taken, and none of them this browser's: say so now,
        // rather than after a microphone check and a latency test for a game
        // this visitor cannot join. A page holding a claim on a held seat is
        // the person it is held for, and goes on to take it back.
        if (roomIsFullFor(summary, hasStoredSeat(roomCode))) {
          setRoomSeats(summary.playerCount)
          setStage('full')
          return HANDLED
        }
        return summary.playerCount
      })
      .catch(() => {
        // The `/api/config` fetch below reports an unreachable server; a second
        // sentence saying the same thing is noise. Fall back to the box default.
        return null
      })
      .then((count) => {
        if (cancelled || count === HANDLED) return
        // Decided in the same update that learns the seat count, so the mic
        // gate is never drawn for a render before the question it would be
        // skipped for is answered. One seat means no partner to talk to, so the
        // microphone is never asked for; more than one means recording, which
        // is agreed to first.
        const next = firstStage(count, storedConsent() !== null)
        setRoomSeats(count)
        setAskedConsent(next === 'consent')
        setStage((current) => (current === 'mic' ? next : current))
      })
    return () => {
      cancelled = true
    }
  }, [roomCode, closeRoom])

  // The socket opens once and outlives every stage: the latency gate needs it
  // before a seat exists, and the round needs the same one afterwards.
  useEffect(() => {
    const socket = connect(
      feed,
      {
        onRoom: setRoom,
        onError: (message) => {
          // The server would not seat this browser without a current
          // agreement — the stored one is stale, or was never there. Forget it
          // and ask again, rather than leave the player looking at a lobby
          // they are not in.
          if (message === CONSENT_REFUSED) {
            clearConsent()
            consentRef.current = null
            // The microphone is asked for again after the agreement, in order;
            // the one already open is let go rather than left running.
            micStreamRef.current?.getTracks().forEach((track) => track.stop())
            micStreamRef.current = null
            setMicStream(null)
            setAskedConsent(true)
            setStage('consent')
          }
          // Somebody took the last seat between the lookup and the join. That
          // is the end of this page, said on a screen of its own — not a red
          // line on top of a lobby that reads "0 of 2 players" and invites the
          // visitor to share the code of a game they are not in.
          if (isRoomFullMessage(message)) {
            setStage('full')
            return
          }
          setError(message)
        },
        onCredentials: setIce,
        // A seat reclaimed from storage is a player who has already been
        // through every gate this page has. Sending them back to the
        // microphone prompt while their chef stands in a running round would
        // be the reload costing them the thing the reload was meant to save.
        onResumed: () => {
          resumedRef.current = true
          setStage('playing')
        },
        onCountdown: setCountdown,
        onRoomClosed: ({ reason }) => closeRoom(reason),
        /**
         * The other chef's line, read aloud.
         *
         * `shouldSpeak` is what keeps this honest: only the *other* seat, and
         * only a line that left on the `tts` channel. Our own utterance coming
         * back off the frame stream would be the game talking to itself, and a
         * `text`-channel line belongs to an AI-AI round, which is defined by
         * having no audio in it at all. The voice is null in a human-human round,
         * so nothing here fires.
         */
        onUtterance: (event) => {
          const mouth = agentVoiceRef.current
          if (!mouth) return
          if (!shouldSpeak(event, selfIdRef.current)) return
          mouth.say(event.text)
        },
      },
      { roomCode },
    )
    setConnection(socket)
    return () => {
      socket.disconnect()
      setConnection(null)
    }
  }, [feed, roomCode, closeRoom])

  // The feed needs to know which chef is this one, so a refused press produces
  // a sentence for the player who pressed and silence for the other.
  useEffect(() => {
    feed.setSelfId(room.playerId)
  }, [feed, room.playerId])

  // A refused input reaches the same flash line an engine refusal does — the
  // player asked one question, "why did nothing happen?", and should not need
  // two places to look for the answer. It is also held in React state for the
  // screens that have no HUD.
  useEffect(() => {
    if (!connection) return
    return connection.onInputRejected((reason) => {
      feed.refuse(reason)
      setRejected(reason)
    })
  }, [connection, feed])

  // A round that is under way has no count left to run. Belt and braces for the
  // frame that carried `ROUND_STARTED` arriving while this tab was elsewhere.
  useEffect(() => {
    if (room.phase !== 'lobby') setCountdown(null)
  }, [room.phase])

  useEffect(() => {
    if (!rejected) return
    const timer = setTimeout(() => setRejected(null), REFUSAL_LIFETIME_MS)
    return () => clearTimeout(timer)
  }, [rejected])

  /**
   * "I am through the gates."
   *
   * Sent once, when the last setup screen clears. The server holds a forced
   * round start while a seat is still in setup, so this is what stops a player
   * being dropped into a running round with no key listeners attached — the
   * failure that looks, from the inside, exactly like the controls not working.
   */
  const setupReported = useRef(false)
  useEffect(() => {
    if (stage !== 'playing' || !room.playerId || setupReported.current) return
    setupReported.current = true
    connectionRef.current?.setupDone()
  }, [stage, room.playerId])

  const peer = room.players.find((p) => p.id !== room.playerId) ?? null
  const peerId = peer?.id ?? null
  /**
   * Whether the other seat is a program.
   *
   * Absent `kind` reads as human, which is what every seat was before this
   * phase and what a browser could only ever be.
   */
  const peerIsAgent = peer?.kind === 'agent'
  /**
   * The other seat's peer connection — `null` when there is nobody to negotiate
   * with.
   *
   * This is the whole client half of the human-AI bridge, and it is a
   * subtraction. An agent has no microphone and no `RTCPeerConnection`; offering
   * one would be an offer nothing answers, three seconds of ICE gathering, and a
   * `failed` status on screen during a recorded round. The agent's half of the
   * conversation arrives as `UTTERANCE` events on the frame stream and is read
   * aloud below, which needs no media path at all.
   *
   * Everything else on this page is untouched by it, on purpose: the same mic
   * check, the same latency gate, the same tutorial, the same recording streamed
   * to the same key. The player's side of a human-AI round differs from a
   * human-human round in who is on the other side and in nothing else.
   */
  const voicePeerId = peerIsAgent ? null : peerId

  /**
   * Give the agent seat a voice for the length of the round.
   *
   * Built when the round is on and the other seat is a program, and silenced on
   * the way out — `speechSynthesis` outlives a React tree, so a queue left
   * running would go on talking over the end screen. Autoplay policy is already
   * satisfied by the time this mounts: the player clicked through the mic check,
   * which is the gesture.
   */
  useEffect(() => {
    if (stage !== 'playing' || !peerIsAgent) return
    const mouth = createAgentVoice()
    agentVoiceRef.current = mouth
    if (mouth.mute) {
      console.warn('[voice] this browser has no speech synthesis — the agent will be read, not heard')
    }
    return () => {
      mouth.silence()
      agentVoiceRef.current = null
    }
  }, [stage, peerIsAgent])

  const sendSignal = useCallback<NonNullable<Connection['sendSignal']>>((to, signal) => {
    connectionRef.current?.sendSignal(to, signal)
  }, [])
  const onSignal = useCallback<Connection['onSignal']>(
    (handler) => connectionRef.current?.onSignal(handler) ?? (() => undefined),
    [],
  )
  const requestCredentials = useCallback(() => connectionRef.current?.requestCredentials(), [])
  const onRttSample = useCallback((rttMs: number) => connectionRef.current?.reportRtt(rttMs), [])

  const voice = usePeerVoice({
    localStream: stage === 'playing' ? micStream : null,
    selfId: room.playerId,
    peerId: voicePeerId,
    ice,
    sendSignal,
    onSignal,
    requestCredentials,
    sampleIntervalMs: config?.latency.voiceSampleIntervalMs ?? 5000,
    onRttSample,
  })
  useVoiceStateReport(connectionRef, voice.status, voice.relayed, room.phase)

  /**
   * The reload path asks for the microphone again.
   *
   * A tab that reclaims its seat skips every gate — rightly: its chef is
   * standing in a running round — and the mic check was one of them, so the
   * page came back with no microphone at all. Nothing was recorded for the
   * rest of the round and voice never reconnected, because both hang off the
   * stream the mic check used to hand over. The browser remembers the
   * permission, so asking again is normally silent.
   */
  useEffect(() => {
    if (!resumedRef.current) return
    if (!needsMicrophone({ stage, seats: roomSeats, haveMic: micStream !== null })) return
    let cancelled = false
    void navigator.mediaDevices
      ?.getUserMedia({ audio: MIC_CONSTRAINTS })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        setMicStream(stream)
      })
      .catch((err: Error) => {
        // The round goes on without a recording from this seat; the server's
        // data-quality verdict will say so.
        console.warn('[audio] could not reopen the microphone after a reload:', err.message)
      })
    return () => {
      cancelled = true
    }
  }, [stage, roomSeats, micStream])

  const onMicPass = useCallback((stream: MediaStream) => {
    setMicStream(stream)
    setStage('latency')
  }, [])

  const onLatencyPass = useCallback(
    () => {
      // Straight to the seat: the lesson happened on the landing page, and a
      // player who arrives here is a player who is ready to cook.
      setStage('playing')
      // A resumed tab is seated already; joining again would take a second seat
      // and strand the chef the round is actually driving.
      if (resumedRef.current) return
      // A join never creates the room, so an unknown code comes back as
      // `roomClosed` and the page ends there. The map is still sent for an
      // older server; the current one ignores it. The recording agreement
      // rides along: a room with more than one seat is refused without it.
      connectionRef.current?.join(roomCode, mapName, consentRef.current ?? storedConsent())
    },
    [roomCode, mapName],
  )

  const onConsentAgree = useCallback(() => {
    consentRef.current = saveConsent()
    setError(null)
    setStage('mic')
  }, [])

  const onConsentDecline = useCallback(() => {
    setStage('declined')
  }, [])

  const onLatencyFail = useCallback(() => {
    // The gate component shows the explanation and the retry; nothing is
    // recorded here, because a failed gate is not a session.
  }, [])

  const self = room.players.find((p) => p.id === room.playerId)
  const iAmReady = self?.ready ?? false
  const peerInSetup = room.players.some((p) => p.id !== room.playerId && p.inSetup)

  // Once a round has been played, readiness stops being automatic: a second
  // round is something both people have to ask for, on the end screen.
  const playedOnce = useRef(false)
  useEffect(() => {
    if (room.phase === 'playing') playedOnce.current = true
  }, [room.phase])

  /**
   * Reporting ready is what starts the round timer, so it waits for voice.
   *
   * Voice should be connected before the timer starts, and the timer is
   * finite — three minutes spent negotiating ICE would be
   * three minutes of the game. But a failed negotiation must not cost the
   * session either, so the wait is bounded by `voiceReadyGraceMs` from
   * `timings.json`, and the server has its own longer backstop on top.
   */
  useEffect(() => {
    if (stage !== 'playing' || room.phase !== 'lobby') return
    if (!room.playerId || !config || iAmReady || playedOnce.current) return

    // Solo has no voice to wait for, and waiting `voiceReadyGraceMs` for a
    // negotiation that will never be attempted would be six seconds of a
    // player staring at an empty lobby they are the only occupant of. An agent
    // in the other seat is the same situation for the same reason: it has no
    // microphone, so there is no call to wait for and the grace window would be
    // six seconds of a finite round spent negotiating with a program.
    if (solo || peerIsAgent || voice.status === 'connected' || voice.status === 'failed') {
      connectionRef.current?.ready()
      return
    }

    const timer = setTimeout(() => connectionRef.current?.ready(), config.voiceReadyGraceMs)
    return () => clearTimeout(timer)
  }, [stage, room.phase, room.playerId, iAmReady, config, voice.status, solo, peerIsAgent])

  const onPlayAgain = useCallback(() => connectionRef.current?.ready(), [])

  /**
   * The recipe panel.
   *
   * Its open state lives here rather than in the HUD because the key that
   * toggles it is bound below, beside the movement keys — one listener owns the
   * keyboard during a round, and a second one racing it for `r` is exactly how
   * a control stops working for some people and not others.
   *
   * Discrete: it changes a handful of times a round, so it goes through React
   * like the rest of the HUD's discrete model. The ref beside it is only so a
   * movement key can close the panel without the listener depending on the
   * state and re-subscribing on every press.
   */
  const [recipesOpen, setRecipesOpen] = useState(false)
  const recipesOpenRef = useRef(false)
  recipesOpenRef.current = recipesOpen
  const toggleRecipes = useCallback(() => setRecipesOpen((open) => !open), [])
  const closeRecipes = useCallback(() => setRecipesOpen(false), [])

  // The panel shows what *this* kitchen can order, not every dish the server
  // knows: a recipe for a dish this map never spawns is noise on a running
  // clock. Empty until `/api/config` and the room state have both landed, which
  // the panel reads as "the whole catalogue".
  const orderPool = useMemo(
    () => config?.maps.find((map) => map.name === room.mapName)?.orderPool ?? [],
    [config, room.mapName],
  )

  // Open at the first whistle for a player who did not skip the tutorial, and
  // once only — a player who closes it has answered the question, and reopening
  // it under them mid-service would be the panel arguing back.
  const autoOpened = useRef(false)
  useEffect(() => {
    if (room.phase !== 'playing' || autoOpened.current) return
    autoOpened.current = true
    if (prefersRecipesOpen()) setRecipesOpen(true)
  }, [room.phase])

  // The tutorial teaches the kitchen actually being played: an oven is
  // explained on a map that has one and never mentioned on a map that does not.
  // `room.mapName` is the server's answer and the query string is only the
  // request, so the confirmed name wins as soon as the join lands.
  const kitchenName = room.mapName ?? mapName
  const tutorialSteps = useMemo(() => stepsFor(config, kitchenName), [config, kitchenName])

  const recipes = useMemo<RecipesControl>(
    () => ({ open: recipesOpen, pool: orderPool, toggle: toggleRecipes, close: closeRecipes }),
    [recipesOpen, orderPool, toggleRecipes, closeRecipes],
  )

  // Only while the round is actually running. Three conditions rather than one,
  // and each of them is a screen this handler must not be on: `stage` covers the
  // gates, where swallowing Space would break the keyboard path to "my
  // microphone is working"; `room.phase` and `ended` cover the other end, where
  // it swallowed Space on <RoundEnd>'s "Play again" and on the lobby's buttons
  // behind it — `preventDefault()` on a focused button is a button that cannot
  // be pressed, and an interact sent into a round that is over.
  // `PracticeRound.tsx` has always gated on its own outcome; this is that.
  useEffect(() => {
    if (!roundKeysActive({ stage, phase: room.phase, ended: Boolean(ended) })) return

    const onKeyDown = (event: KeyboardEvent): void => {
      const connection = connectionRef.current
      if (!connection) return
      // Cmd+R, Ctrl+S, Cmd+A: the browser's keys and the operating system's,
      // never the kitchen's. Without this the panel toggle ate a reload and
      // select-all walked the chef.
      if (isSystemChord(event)) return

      const direction = KEY_TO_DIRECTION[event.key]
      if (direction) {
        event.preventDefault()
        // The recipe panel is a reference, not a mode: walking away from it is
        // how a person closes a book. The same press does both, so nobody has
        // to dismiss anything before their chef will move.
        if (recipesOpenRef.current) setRecipesOpen(false)
        connection.move(direction)
        return
      }
      if (event.key === ' ') {
        event.preventDefault()
        if (!event.repeat) connection.interact()
        return
      }
      // `r` is unbound — movement is the arrows plus WASD, interact is Space —
      // so nothing that already worked changes meaning. It toggles rather than
      // opens, because the key you opened something with is the key you reach
      // for to shut it.
      if (event.key === 'r' || event.key === 'R') {
        event.preventDefault()
        if (!event.repeat) setRecipesOpen((open) => !open)
        return
      }
      if (event.key === 'Escape' && recipesOpenRef.current) {
        event.preventDefault()
        setRecipesOpen(false)
      }
    }

    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === ' ') connectionRef.current?.interactRelease()
    }

    // A window that loses focus never delivers the keyup, and the server would
    // go on chopping for as long as the tab stayed in the background. Treat
    // losing focus as letting go.
    const onBlur = (): void => connectionRef.current?.interactRelease()

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
      // The whistle can land with the key still down, and the handler that would
      // have seen the keyup is being removed right now. Let go on the way out,
      // for the same reason `onBlur` does.
      connectionRef.current?.interactRelease()
    }
  }, [stage, room.phase, ended])

  const seat = self?.seat
  // The room's own seat count when the lookup found one, the box's default
  // otherwise. Never a number this component decided: solo is a property of
  // the room the host created, not of the browser looking at it.
  const seats = roomSeats ?? config?.playerCount ?? 2
  const steps = gateSteps(solo, askedConsent)

  /**
   * The fallback for the case the setup hold cannot prevent: the grace cap ran
   * out and the round started without this player. Said plainly, because the
   * alternative — which is what shipped before — is a person pressing keys at a
   * screen that will never respond and concluding the game is broken.
   */
  const missedTheStart = room.phase === 'playing' && stage !== 'playing'

  // The flash line lives in the HUD, and the HUD is only up during a round.
  // Everywhere else, a refused press says so here instead.
  const hudVisible = stage === 'playing' && room.phase === 'playing' && !ended
  // The card for a round this page saw end, or for one that ended before it
  // arrived — see `endCardFor`.
  const endCard = endCardFor(room.phase, ended)
  const rejectionNotice =
    rejected && !hudVisible ? (
      <p className="hud-flash hud-flash-refusal page-flash" role="status">
        {refusalFor(rejected).phrase}
      </p>
    ) : null

  /**
   * The other chef's connection went away, and their seat is being kept.
   *
   * Worth a sentence of its own: a chef who has stopped moving looks exactly
   * like a chef who is ignoring you, and the difference between "they have
   * gone" and "they are coming back" is the difference between waiting and
   * giving up. The round keeps running underneath it — the seat stands still
   * and the clock does not.
   */
  const dropped = room.players.filter((p) => p.id !== room.playerId && !p.connected)
  const droppedNotice =
    dropped.length > 0 && !ended ? (
      <p className="notice" role="status">
        {dropped.length === 1
          ? `${dropped[0].kind === 'agent' ? 'The AI chef' : 'Your partner'} lost connection. We'll keep their place for a minute.`
          : `${dropped.length} players lost connection. We'll keep their places for a minute.`}
      </p>
    ) : null

  const lateNotice = missedTheStart ? (
    <p className="notice" role="status">
      The round has already started. Finish setting up to join in.
    </p>
  ) : null

  const header = (
    <header className="bar">
      <span className="brand">
        Over<em>cooked</em>
      </span>
      <span className="code">Game {room.roomCode ?? roomCode}</span>
      {room.mapName && <span className="code">{prettyMapName(room.mapName)}</span>}
      {stage === 'playing' && (
        <span>
          {solo
            ? 'Solo'
            : `${room.players.length} of ${seats} players`}
          {seat && !solo ? ` · You're the ${seatIdentity(seat).label.toLowerCase()} chef` : ''}
        </span>
      )}
      {stage === 'playing' && (
        <span className={`phase phase-${room.phase}`}>{phaseWord(room.phase)}</span>
      )}
    </header>
  )

  if (stage === 'closed' && closedReason) {
    return (
      <main className="page">
        {header}
        <RoomClosed reason={closedReason} />
      </main>
    )
  }

  if (stage === 'full') {
    return (
      <main className="page">
        {header}
        <RoomUnavailable message={fullMessage(room.roomCode ?? roomCode)} />
      </main>
    )
  }

  if (stage === 'excluded') {
    return (
      <main className="page">
        {header}
        <section className="gate">
          <h1>You can't join this game</h1>
          <p className="hint">{excluded}</p>
          <p className="hint">Nothing was recorded. Fix the problem, then reload to try again.</p>
        </section>
      </main>
    )
  }

  // Held for one HTTP round trip, deliberately. Whether this room wants a
  // microphone at all is the first thing to know and the mic gate is the first
  // thing drawn, so drawing it before the answer arrives would mean prompting a
  // solo player for permission and then taking the question away again.
  if (stage === 'mic' && roomSeats === undefined) {
    return (
      <main className="page">
        {header}
        <section className="gate">
          <h1>Finding your game…</h1>
          {error && <p className="error">{error}</p>}
        </section>
      </main>
    )
  }

  if (stage === 'consent') {
    return (
      <main className="page">
        {header}
        {lateNotice}
        <ConsentGate
          step={steps.consent}
          of={steps.of}
          error={error}
          onAgree={onConsentAgree}
          onDecline={onConsentDecline}
        />
      </main>
    )
  }

  if (stage === 'declined') {
    return (
      <main className="page">
        {header}
        <ConsentDeclined
          onReview={() => setStage('consent')}
          onLeave={() => window.location.assign('/')}
        />
      </main>
    )
  }

  if (stage === 'mic') {
    return (
      <main className="page">
        {header}
        {error && <p className="error">{error}</p>}
        {lateNotice}
        {rejectionNotice}
        <MicCheck
          step={steps.mic}
          of={steps.of}
          onPass={onMicPass}
          onGiveUp={(reason) => {
            setExcluded(reason)
            setStage('excluded')
          }}
        />
      </main>
    )
  }

  if (stage === 'latency') {
    if (!config || !connection) {
      return (
        <main className="page">
          {header}
          <section className="gate">
            <h1>Connecting…</h1>
            {error && <p className="error">{error}</p>}
          </section>
        </main>
      )
    }
    return (
      <main className="page">
        {header}
        {lateNotice}
        {rejectionNotice}
        <LatencyGate
          transport={connection.ping}
          config={config.latency}
          step={steps.latency}
          of={steps.of}
          onPass={onLatencyPass}
          onFail={onLatencyFail}
        />
      </main>
    )
  }

  return (
    <main className="page">
      {header}

      {error && <p className="error">{error}</p>}
      {rejectionNotice}
      {droppedNotice}

      {room.phase === 'lobby' && !ended && (
        <section className="lobby">
          <span className="lobby-step">
            {countdown !== null
              ? "Everyone's in"
              : solo
                ? 'Solo game'
                : room.players.length < seats
                  ? 'Invite your partner'
                  : "Everyone's here"}
          </span>
          <h2>
            {countdown !== null
              ? 'Get ready…'
              : solo
                ? 'Starting…'
                : room.players.length < seats
                  ? 'Waiting for your partner…'
                  : iAmReady
                    ? peerInSetup
                      ? 'Your partner is setting up…'
                      : 'Waiting for your partner…'
                    : 'Connecting voice…'}
          </h2>
          {/* The starting moment, said out loud. The clock does not begin
              until the count does — three seconds that belong to the two
              people rather than to the round. */}
          {countdown !== null && (
            <p className="seat-countdown" role="status" aria-live="assertive">
              {/* Keyed on the number so each numeral is a new element and the
                  beat animation replays on 2 and 1, not only on 3. */}
              <span key={countdown} className="seat-countdown-value">
                {countdown}
              </span>
              <span className="seat-countdown-cap">until the round starts</span>
            </p>
          )}
          <ol className="seat-list">
            {Array.from({ length: seats }, (_, i) => i + 1).map((n) => {
              const occupant = room.players.find((p) => p.seat === n)
              return (
                <li key={n} className={occupant ? 'seat seat-taken' : 'seat seat-open'}>
                  <span className={`seat-chip seat-chip-${n}`} aria-hidden="true" />
                  <span className="seat-name">
                    {/* An AI in the seat is said to be one. The player is
                        about to talk to it, and "your partner" alone would
                        mislead them about who they are talking to — which is
                        the one thing this condition must not do. */}
                    {occupant
                      ? occupant.id === room.playerId
                        ? 'You'
                        : occupant.kind === 'agent'
                          ? 'AI chef'
                          : 'Your partner'
                      : 'Open'}
                  </span>
                  {/* Three states, not two: a seat still in setup is a person
                      reading a screen, and saying so is the difference between
                      "they are slow" and "they have gone". */}
                  <span className="seat-state">
                    {occupant
                      ? !occupant.connected
                        ? 'reconnecting…'
                        : occupant.ready
                          ? 'ready'
                          : occupant.inSetup
                            ? 'setting up'
                            : 'joining…'
                      : 'empty'}
                  </span>
                </li>
              )
            })}
          </ol>
          {/* The invite, as something to read aloud rather than a URL printed
              as prose. Two ways to hand it over, because a code is what you say
              on a call and a link is what you paste into a chat. */}
          {room.players.length < seats && <InviteCode code={room.roomCode ?? roomCode} />}
          {/* The other way to fill the seat. Only rendered when the server says
              it can, and never in a solo kitchen — one seat has no other one. */}
          {!solo && room.players.length < seats && (
            <AgentSeatInvite code={room.roomCode ?? roomCode} />
          )}
        </section>
      )}

      {/* The wait for the other chef is dead time, and it is the last quiet
          moment before a three-minute clock. The same cards, with no decision
          attached to them: nothing here starts, skips or delays anything. */}
      {room.phase === 'lobby' && !ended && (
        <Tutorial
          steps={tutorialSteps}
          catalog={catalog}
          dishes={config?.dishes ?? []}
          variant="lobby"
        />
      )}

      {endCard && (
        <RoundEnd
          score={endCard.score}
          reason={endCard.reason}
          tally={tally}
          sessionId={room.sessionId}
          waiting={iAmReady}
          onPlayAgain={onPlayAgain}
        />
      )}

      {/* The kitchen exists while there is a round. In the lobby there is
          nothing to draw, and after the whistle the scoreboard is the thing to
          look at — a frozen kitchen underneath it is clutter. */}
      {room.phase === 'playing' && !ended && (
        <Stage feed={feed} catalog={catalog} selfId={room.playerId} showHud recipes={recipes} />
      )}

      {/* A voice readout in a one-seat kitchen would report on a call that was
          never placed. No line here shows latency: that is for the logs, not
          for the person playing. */}
      {solo ? (
        <p className="hint voice-status">Playing solo</p>
      ) : peerIsAgent ? (
        /* No call was placed, so there is no call to report on. What is worth
           saying is that the other chef is an AI — a player who thinks voice
           is broken will stop talking, which would cost the round the very
           thing it is recording. */
        <p className="hint voice-status">Your partner is an AI chef</p>
      ) : (
        <p className="hint voice-status">
          <span className={`dot dot-${voice.status}`} aria-hidden="true" />
          Voice: {VOICE_LABEL[voice.status]}
        </p>
      )}
      {!solo && !peerIsAgent && voice.error && <p className="error">{voice.error}</p>}
    </main>
  )
}

const VOICE_LABEL: Record<string, string> = {
  idle: 'waiting for your partner',
  connecting: 'connecting…',
  connected: 'connected',
  reconnecting: 'reconnecting…',
  failed: 'not working',
}

/**
 * Create, or join with a code somebody read out.
 *
 * The old landing page asked both players to invent the same four characters
 * out of band, which meant a typo and "my friend has not arrived yet" looked
 * identical — an empty room is exactly what `/r/ABCD` produces for a code
 * nobody else used. So the server mints the code now, and the join box asks
 * whether the game exists before it navigates.
 *
 * A typed `/r/ABCD` path only ever joins. A link for a code nobody minted, or
 * for a room that has since closed, ends on the closed screen rather than
 * quietly opening a new room under the same code.
 */
function Landing() {
  const [code, setCode] = useState('')
  const [config, setConfig] = useState<ClientConfig | null>(null)
  /**
   * The lesson, in front of everything.
   *
   * Shown unasked on a first visit and by the button after that. It is not a
   * gate: nobody is seated, nothing is being held for anybody, and the way out
   * is a button on the card. That is what moving it here bought — the tutorial
   * can take as long as it takes.
   */
  const [learning, setLearning] = useState(() => !hasSeenTutorial())
  const seenTutorial = useMemo(() => hasSeenTutorial(), [])
  const [chosen, setChosen] = useState<string>('')
  const [busy, setBusy] = useState<'create' | 'solo' | 'join' | null>(null)
  const [joinError, setJoinError] = useState<string | null>(null)
  /**
   * The study batch this game is for. Asked of whoever creates a game — both
   * "Create a game" and "Play solo" send it — and never of someone joining,
   * who is recorded under the room's code. Prefilled from `?invite=` so an
   * experimenter's link saves the typing; the server is the only judge of it.
   */
  const [invite, setInvite] = useState(() => readInviteQuery(window.location.search))
  const [inviteError, setInviteError] = useState<string | null>(null)

  /**
   * Open a room and go to it. The server mints the code and decides the seat
   * count; both create paths differ only in the body they send. A refused
   * invite is shown under the invite field and the page stays where it is.
   */
  const create = useCallback(
    (which: 'create' | 'solo', body: Record<string, string>) => {
      if (busy) return
      setBusy(which)
      setJoinError(null)
      setInviteError(null)
      void fetch('/api/rooms', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...body, invite }),
      })
        .then(async (response) => {
          if (!response.ok) {
            const refusal = createErrorFrom(response.status, await response.json().catch(() => null))
            setBusy(null)
            if (refusal.aboutInvite) setInviteError(refusal.message)
            else setJoinError(refusal.message)
            return
          }
          const { code: minted } = (await response.json()) as { code: string }
          window.location.href = `/r/${minted}`
        })
        .catch(() => {
          setBusy(null)
          setJoinError(CREATE_FAILED)
        })
    },
    [busy, invite],
  )

  const onCreate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      create('create', chosen ? { map: chosen } : {})
    },
    [create, chosen],
  )

  // No map choice here on purpose: solo is one seat, and one seat on a kitchen
  // built around a counter dividing two people is a game that cannot be
  // finished. The server picks the one layout a lone chef can complete.
  const onSolo = useCallback(() => create('solo', { mode: 'solo' }), [create])

  const onJoin = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      if (busy) return
      const normalised = code.trim().toUpperCase()
      if (!/^[A-Z0-9]{4}$/.test(normalised)) {
        setJoinError('Game codes are four letters or numbers.')
        return
      }
      setBusy('join')
      setJoinError(null)
      // Ask before navigating. Going straight to `/r/CODE` would create the
      // room for a mistyped code and seat the player alone in it, waiting for
      // a partner who is standing in a different kitchen entirely.
      void fetch(`/api/rooms/${normalised}`)
        .then((response) => {
          if (response.status === 404) {
            setBusy(null)
            setJoinError('No game with that code. Check it and try again.')
            return
          }
          if (!response.ok) throw new Error(String(response.status))
          window.location.href = `/r/${normalised}`
        })
        .catch(() => {
          setBusy(null)
          setJoinError("Couldn't reach the game. Check your connection.")
        })
    },
    [busy, code],
  )

  // The presets and the menu come from the server, because which kitchens and
  // which dishes exist is a question `server/src/content/` answers and the
  // bundle should not.
  useEffect(() => {
    let cancelled = false
    void fetch('/api/config')
      .then((response) => response.json() as Promise<ClientConfig>)
      .then((value) => {
        if (cancelled) return
        setConfig(value)
        setChosen(value.defaultMap)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * Each kitchen's oracle score, from the cache, for the picker. Asked only
   * once the picker itself is filled, so the page never waits on it; a fetch
   * that fails leaves the options as they were.
   */
  const [oracle, setOracle] = useState<ReturnType<typeof oracleByMap>>(() => oracleByMap(null))
  useEffect(() => {
    if (!config) return
    let cancelled = false
    void fetchOracleList().then((rows) => {
      if (!cancelled && rows) setOracle(oracleByMap(rows))
    })
    return () => {
      cancelled = true
    }
  }, [config])

  const catalog = useMemo(() => buildCatalog(config), [config])
  // The picker offers kitchens for two. The solo kitchen is reached by the
  // solo button and would be a strange thing to pick for a game with a guest
  // in it — it has one spawn, so the guest would have nowhere to stand.
  const maps = useMemo(() => (config?.maps ?? []).filter((m) => m.seats >= 2), [config])
  const chosenMap = maps.find((m) => m.name === chosen)
  const menu = (chosenMap?.orderPool ?? []).map((id) => catalog.dish(id)).filter(Boolean)

  // The practice kitchen is the sandbox's, not the server's: it teaches a
  // kitchen the player can stand in right now, with no network in the way.
  // Explaining a carrot to somebody holding meat is exactly the drift an
  // interactive tutorial exists to remove.
  const sandboxSteps = useMemo(() => stepsFor(TUTORIAL_CONFIG, TUTORIAL_MAP_NAME), [])
  const sandboxCatalog = useMemo(() => buildCatalog(TUTORIAL_CONFIG), [])

  const bar = (
    <header className="bar">
      <span className="brand">
        Over<em>cooked</em>
      </span>
      {/* A visible way in. The replay page has been reachable only by typing
          the URL, which is the same as not existing for everyone who was not
          told about it. */}
      <a className="bar-link" href="/replay">
        Replay
      </a>
      {/* The oracle playing a kitchen: the best round two coordinated chefs
          can play there, to compare a real round with. */}
      <a className="bar-link" href="/oracle">
        Oracle
      </a>
    </header>
  )

  if (learning) {
    return (
      <main className="page landing-page">
        {bar}
        <Tutorial
          steps={sandboxSteps}
          catalog={sandboxCatalog}
          dishes={TUTORIAL_CONFIG.dishes}
          lead="How to play"
          seen={seenTutorial}
          interactive
          // The landing page is the one place a five-minute solo round costs
          // nobody anything: no seat is held, no partner is waiting, and the
          // way out is a button on the card the whole time.
          practice
          onDone={() => setLearning(false)}
        />
      </main>
    )
  }

  return (
    <main className="page landing-page">
      {bar}

      <section className="hero">
        <div className="hero-copy">
        <h1>
          Orders in.
          <br />
          Pans on.
          <br />
          <em>Talk fast.</em>
        </h1>
        <p className="hero-lede">A cooking game for two. You each run half the kitchen, so keep talking.</p>

        {/* Host first, guest second. The person who picks the kitchen is the
            person who has something to share, so the kitchen picker and the
            create button are one row and the code box is its own. */}
        <form className="landing" onSubmit={onCreate}>
          <div className="field">
            <label htmlFor="map">Kitchen</label>
            <select id="map" value={chosen} onChange={(event) => setChosen(event.target.value)}>
              {maps.map((map) => {
                const suffix = lobbyOracleSuffix(oracle.get(map.name))
                return (
                  <option key={map.name} value={map.name}>
                    {prettyMapName(map.name)} · {map.width} × {map.height} ·{' '}
                    {map.orderPool.length} dish{map.orderPool.length === 1 ? '' : 'es'}
                    {suffix ? ` · ${suffix}` : ''}
                  </option>
                )
              })}
            </select>
            {/* The best round two coordinated chefs found on this kitchen,
                played on the real renderer: something to compare a game with. */}
            {chosen && (
              <a className="landing-oracle-link" data-testid="watch-oracle" href={oraclePathFor(chosen)}>
                Watch a perfect round
              </a>
            )}
          </div>
          {/* The invite sits with the create button because creating is what
              it is for; "Play solo" below uses the same value. */}
          <div className="field landing-invite">
            <label htmlFor="invite">Invite code</label>
            <input
              id="invite"
              name="invite"
              value={invite}
              maxLength={24}
              placeholder="PILOT-A"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-invalid={inviteError !== null}
              aria-describedby={inviteError ? 'invite-error' : undefined}
              onChange={(event) => {
                setInvite(event.target.value)
                setInviteError(null)
              }}
            />
          </div>
          <button type="submit" disabled={busy !== null}>
            {busy === 'create' ? 'Starting…' : 'Start a game'}
          </button>
        </form>
        {inviteError && (
          <p className="error" id="invite-error" role="alert">
            {inviteError}
          </p>
        )}

        <form className="landing landing-join" onSubmit={onJoin}>
          <div className="field">
            <label htmlFor="code">Got a game code?</label>
            <input
              id="code"
              value={code}
              maxLength={4}
              placeholder="ABCD"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={joinError !== null}
              onChange={(event) => {
                setCode(event.target.value)
                setJoinError(null)
              }}
            />
          </div>
          <button type="submit" className="ghost" disabled={busy !== null || code.trim() === ''}>
            {busy === 'join' ? 'Checking…' : 'Join'}
          </button>
        </form>
        {/* Third, and last, because it is the smaller game: one seat, one
            kitchen laid out so a single pair of hands can finish a dish. It is
            how you learn the controls without an audience, and how anyone can
            tell in ninety seconds whether the deployment actually works. */}
        <form className="landing landing-solo" onSubmit={(event) => { event.preventDefault(); onSolo() }}>
          <div className="field">
            <span className="field-label">Playing alone?</span>
            <p className="hint">Play a full round by yourself in a small kitchen.</p>
          </div>
          <button type="submit" className="ghost" disabled={busy !== null}>
            {busy === 'solo' ? 'Starting…' : 'Play solo'}
          </button>
        </form>

        {/* Fourth, and the one a first-time visitor was shown before they got
            here. Kept in the list rather than tucked in the header because
            "how do I play this" is a question people ask on the way in, not one
            they go looking in a menu bar for. */}
        <form
          className="landing landing-learn"
          onSubmit={(event) => {
            event.preventDefault()
            setLearning(true)
          }}
        >
          <div className="field">
            <span className="field-label">New here?</span>
            <p className="hint">Practice the basics. No timer, no partner.</p>
          </div>
          <button type="submit" className="ghost">
            Learn to play
          </button>
        </form>

        {joinError && <p className="error">{joinError}</p>}
        </div>

      {menu.length > 0 && (
        <aside className="menu">
          <h2>On the menu</h2>
          <ul className="menu-list">
            {menu.map((dish) =>
              dish ? (
                <li className="menu-card" key={dish.id}>
                  <span className="menu-name">
                    {dish.label}
                    <BonusChip bonus={dish.bonus} />
                  </span>
                  <div className="menu-icons">
                    {dish.requires.items.map((item, i) => (
                      <IconCanvas
                        key={`${item.id}-${i}`}
                        className="ticket-icon"
                        size={38}
                        signature={`menu-${item.source}`}
                        title={catalog.ingredient(item.source).label}
                        draw={(ctx, size) =>
                          drawIngredient(
                            ctx,
                            catalog.ingredient(item.source),
                            size / 2,
                            size / 2,
                            size * 0.42,
                          )
                        }
                      />
                    ))}
                    <span className="menu-arrow" aria-hidden="true" />
                    {dish.requires.containers.map((kind) => (
                      <IconCanvas
                        key={kind}
                        className="ticket-kit-icon"
                        size={28}
                        signature={`menu-container-${kind}`}
                        title={catalog.containerLabel(kind)}
                        draw={(ctx, size) =>
                          drawContainer(
                            ctx,
                            {
                              id: kind,
                              kind,
                              contents: [],
                              cookState: 'empty',
                              phaseStartedAtTick: null,
                              phaseEndsAtTick: null,
                            },
                            size / 2,
                            size / 2,
                            size * 0.4,
                            catalog,
                          )
                        }
                      />
                    ))}
                  </div>
                </li>
              ) : null,
            )}
          </ul>
        </aside>
      )}
      </section>
    </main>
  )
}

export default function App() {
  const path = window.location.pathname
  const roomPath = parseRoomPath(path)

  const requestedMap = new URLSearchParams(window.location.search).get('map')

  if (roomPath?.kind === 'room') return <RoomPage roomCode={roomPath.code} mapName={requestedMap} />
  // `/r/bad!code` used to fall through to the landing page with the bad URL
  // still in the bar and not a word about it.
  if (roomPath?.kind === 'malformed') {
    return (
      <main className="page">
        <header className="bar">
          <span className="brand">
            Over<em>cooked</em>
          </span>
        </header>
        <RoomUnavailable message={malformedCodeMessage(roomPath.text)} />
      </main>
    )
  }
  // `/replay` lists; `/replay/<id>` opens one. A session id is what makes
  // "watch this" a link somebody can be sent, and the production catch-all
  // already serves index.html for anything outside /api and /socket.io, so the
  // deeper path costs no server change.
  const replayMatch = REPLAY_PATH.exec(path)
  if (replayMatch) return <ReplayPage sessionId={replayMatch[1] ?? null} />
  // `/oracle` opens the default kitchen; `/oracle/<map>` a chosen one. Like
  // `/replay`, a client route the production catch-all already serves.
  const oracleRoute = parseOraclePath(path)
  if (oracleRoute) return <OraclePage route={oracleRoute} />
  // `/editor` needs no server state at all beyond the recipe book, which is why
  // it is a route here rather than a tool anywhere else: the engine is already
  // in this bundle, so the validator the server boots on is already in the tab.
  if (EDITOR_PATH.test(path)) return <EditorPage />
  // `/admin` is a password and three lists over the endpoints the editor's save
  // button already uses. It is a route here for the same reason `/editor` is —
  // the catch-all serves `index.html` for anything outside `/api` and
  // `/socket.io`, so a management page costs no server route of its own.
  if (ADMIN_PATH.test(path)) return <AdminPage />
  // Prolific study mode: its own page, its own admin area. Additive — every
  // route above is matched exactly as it was before these two existed.
  if (STUDY_ADMIN_PATH.test(path)) return <StudyAdminPage />
  if (STUDY_PATH.test(path)) return <StudyApp />
  return <Landing />
}
